import { useState } from 'react';
import { makeStyles } from '@material-ui/core/styles';
import { Alert } from '@material-ui/lab';
import Icon from '@mdi/react';

import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  Typography,
  IconButton,
  CircularProgress,
} from '@material-ui/core';

import { mdiClose } from '@mdi/js';

const useStyles = makeStyles((theme) => ({
  dialogTitle: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    padding: theme.spacing(2, 2, 2, 3),
  },
  titleText: {
    flex: 1,
  },
  closeButton: {
    color: theme.palette.grey[500],
    padding: theme.spacing(0.5),
  },
  content: {
    paddingTop: theme.spacing(2),
  },
  alert: {
    marginBottom: theme.spacing(2),
  },
}));

const ShareModal = ({ open, onClose, onShare, title, itemType }) => {
  const classes = useStyles();
  const [email, setEmail] = useState('');
  const [error, setError] = useState('');
  const [successMessage, setSuccessMessage] = useState('');
  const [isLoading, setIsLoading] = useState(false);

  const handleClose = () => {
    setEmail('');
    setError('');
    setSuccessMessage('');
    onClose();
  };

  const validateEmail = (email) => {
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    return emailRegex.test(email);
  };

  const handleShare = async () => {
    setError('');
    setSuccessMessage('');

    if (!email.trim()) {
      setError('Email address is required');
      return;
    }

    if (!validateEmail(email.trim())) {
      setError('Please enter a valid email address');
      return;
    }

    setIsLoading(true);
    try {
      const result = await onShare(email.trim());

      if (result.success) {
        setSuccessMessage(`${itemType} shared successfully with ${email}`);
        setTimeout(() => {
          handleClose();
        }, 1500);
      } else {
        setError(result.message || 'Failed to share. Please try again.');
      }
    } catch (err) {
      setError('An error occurred. Please try again.');
    } finally {
      setIsLoading(false);
    }
  };

  const handleKeyDown = (e) => {
    if (e.key === 'Enter' && !isLoading) {
      e.preventDefault();
      handleShare();
    }
  };

  return (
    <Dialog
      open={open}
      onClose={handleClose}
      maxWidth="xs"
      fullWidth
    >
      <DialogTitle disableTypography className={classes.dialogTitle}>
        <Typography variant="h6" className={classes.titleText}>
          {title || 'SHARE WITH'}
        </Typography>
        <IconButton
          className={classes.closeButton}
          onClick={handleClose}
          disabled={isLoading}
        >
          <Icon path={mdiClose} size={1} />
        </IconButton>
      </DialogTitle>

      <DialogContent className={classes.content}>
        {error && (
          <Alert severity="error" className={classes.alert}>
            {error}
          </Alert>
        )}

        {successMessage && (
          <Alert severity="success" className={classes.alert}>
            {successMessage}
          </Alert>
        )}

        <TextField
          autoFocus
          fullWidth
          label="Email Address"
          type="email"
          variant="outlined"
          value={email}
          onChange={(e) => setEmail(e.target.value)}
          onKeyDown={handleKeyDown}
          disabled={isLoading}
          placeholder="example@email.com"
        />
      </DialogContent>

      <DialogActions>
        <Button
          onClick={handleClose}
          disabled={isLoading}
        >
          Cancel
        </Button>
        <Button
          onClick={handleShare}
          color="primary"
          variant="contained"
          disabled={isLoading}
        >
          {isLoading ? <CircularProgress size={24} /> : 'SHARE'}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default ShareModal;